import React from 'react';
import styled from 'styled-components';

// Styled components for the content
const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.theme?.name === 'dark' ? '#E5E7EB' : '#374151')};
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const SectionContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${(props) => (props.theme?.name === 'dark' ? '#CBD5E1' : '#6B7280')};

  strong {
    color: ${(props) => (props.theme?.name === 'dark' ? '#ffffff' : '#1F2937')};
    font-weight: 600;
  }

  p {
    margin: 12px 0;
  }

  ul {
    margin: 12px 0;
    padding-left: 20px;
    list-style: none;

    li {
      position: relative;
      margin: 10px 0;
      padding-left: 16px;

      &::before {
        content: '•';
        position: absolute;
        left: 0;
        color: #fa4616;
        font-weight: bold;
      }
    }
  }

  ol {
    margin: 12px 0;
    padding-left: 24px;

    li {
      margin: 10px 0;
    }
  }

  br {
    line-height: 1.8;
  }
`;

const FlowDiagram = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0;
  padding: 20px;
  background: ${(props) =>
    props.theme?.name === 'dark'
      ? 'rgba(255,255,255,0.02)'
      : 'rgba(0,0,0,0.02)'};
  border-radius: 8px;
`;

const FlowStep = styled.div`
  flex: 1;
  text-align: center;
`;

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  background: #fa4616;
  color: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  margin: 0 auto 8px;
`;

const StepText = styled.div`
  font-size: 12px;
  color: ${(props) => (props.theme?.name === 'dark' ? '#CBD5E1' : '#6B7280')};
`;

const FlowArrow = styled.div`
  width: 40px;
  height: 2px;
  background: #fa4616;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid #fa4616;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
  }
`;

const CalloutBox = styled.div`
  background: ${(props) =>
    props.theme?.name === 'dark'
      ? 'rgba(59, 130, 246, 0.1)'
      : 'rgba(59, 130, 246, 0.05)'};
  border-left: 3px solid #3b82f6;
  padding: 16px;
  margin: 20px 0;
  border-radius: 4px;
`;

const CalloutText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${(props) => (props.theme?.name === 'dark' ? '#E5E7EB' : '#374151')};
  margin: 0;
`;

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    rgba(250, 70, 22, 0.1) 0%,
    rgba(250, 70, 22, 0.05) 100%
  );
  border: 1px solid rgba(250, 70, 22, 0.2);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 32px;
`;

const HeroTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fa4616;
  margin-bottom: 8px;
`;

const HeroText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${(props) => (props.theme?.name === 'dark' ? '#E5E7EB' : '#374151')};
  margin: 0;
`;

// Export the content as a React component
export const SmartInsightsContent = () => (
  <>
    <HeroSection>
      <HeroTitle>Different Customers, Different Needs</HeroTitle>
      <HeroText>
        Every successful business recognizes this simple truth. Airlines have
        loyalty tiers. Retailers send birthday discounts. Restaurants take care
        of regulars. Different customers respond to different messages - use
        that to your advantage.
      </HeroText>
    </HeroSection>

    <Section>
      <SectionTitle>How Targeted Campaigns Work</SectionTitle>
      <SectionContent>
        <p>
          Connect your customer filtering with Quick Actions to create powerful,
          targeted campaigns that get results.
        </p>
        <FlowDiagram>
          <FlowStep>
            <StepNumber>1</StepNumber>
            <StepText>Filter Table</StepText>
          </FlowStep>
          <FlowArrow />
          <FlowStep>
            <StepNumber>2</StepNumber>
            <StepText>Quick Actions</StepText>
          </FlowStep>
          <FlowArrow />
          <FlowStep>
            <StepNumber>3</StepNumber>
            <StepText>Send Campaign</StepText>
          </FlowStep>
        </FlowDiagram>
        <p>
          Whatever filters you apply to the customer table automatically become
          your campaign audience. No extra steps needed.
        </p>
      </SectionContent>
    </Section>

    <Section>
      <SectionTitle>Why This Matters</SectionTitle>
      <SectionContent>
        <p>
          <strong>People are creatures of habit.</strong> Once they stop coming,
          it gets easier to keep not coming. Don't let customers drift away.
        </p>
        <ul>
          <li>
            <strong>Better results</strong> - Send the right message to the
            right people instead of hoping a generic message works for everyone
          </li>
          <li>
            <strong>Lower costs</strong> - Stop wasting credits on messages to
            people who won't respond
          </li>
          <li>
            <strong>Customer retention</strong> - Stay top of mind so customers
            don't forget about you when planning their weekend
          </li>
        </ul>
        <CalloutBox>
          <CalloutText>
            <strong>Think about it:</strong> It's always cheaper to keep a
            customer than to win them back after they've left. A small thank-you
            to regulars costs less than a big discount to win back someone who's
            been gone for months.
          </CalloutText>
        </CalloutBox>
      </SectionContent>
    </Section>

    <Section>
      <SectionTitle>Win-Back Campaigns</SectionTitle>
      <SectionContent>
        <p>
          Re-engage customers who haven't visited recently. Life gets busy -
          help them remember why they loved coming.
        </p>
        <ul>
          <li>
            <strong>Filter:</strong> Customers inactive for 60+ days
          </li>
          <li>
            <strong>Message:</strong> "We miss you" with comeback offer
          </li>
          <li>
            <strong>Why it works:</strong> People get busy and forget. A
            friendly reminder with a small incentive breaks the inertia
          </li>
        </ul>
        <p>
          The longer someone stays away, the harder it is to come back. They
          might feel awkward, assume things have changed, or just develop new
          weekend routines. Reach out before they forget about you completely.
        </p>
      </SectionContent>
    </Section>

    <Section>
      <SectionTitle>Loyalty Rewards</SectionTitle>
      <SectionContent>
        <p>Show appreciation to your best customers before they drift away.</p>
        <ul>
          <li>
            <strong>Filter:</strong> High attendance or high lifetime value
          </li>
          <li>
            <strong>Message:</strong> Thank you credit or exclusive perks
          </li>
          <li>
            <strong>Result:</strong> Increased retention and word-of-mouth
          </li>
        </ul>
        <p>
          Regular customers who feel appreciated stay regular customers. Those
          who feel taken for granted eventually stop making the effort. A simple
          "thanks for being a regular" goes a long way.
        </p>
      </SectionContent>
    </Section>

    <Section>
      <SectionTitle>Location-Based Targeting</SectionTitle>
      <SectionContent>
        <p>Maximize attendance by targeting based on distance.</p>
        <ul>
          <li>
            <strong>Local (0-10 miles):</strong> Last-minute reminders work
            best, weather-based promotions
          </li>
          <li>
            <strong>Nearby (11-60 miles):</strong> Standard event marketing,
            weekend promotions
          </li>
          <li>
            <strong>Regional (61-100 miles):</strong> Need advance notice and
            stronger incentives, weekend packages
          </li>
          <li>
            <strong>Distant (101+ miles, US):</strong> Target for major events,
            vacation packages with hotels
          </li>
          <li>
            <strong>International:</strong> Tourism packages, multi-day passes,
            partner with travel agencies
          </li>
        </ul>
      </SectionContent>
    </Section>

    <Section>
      <SectionTitle>Common Questions</SectionTitle>
      <SectionContent>
        <p>
          <strong>"Isn't this unfair to other customers?"</strong>
        </p>
        <p>
          No. This is how every successful business operates. Different
          customers have different needs. A new customer needs directions and
          information. A regular wants recognition. Someone far away needs more
          notice. It's just good business sense.
        </p>
        <br />
        <p>
          <strong>"What if customers complain about different offers?"</strong>
        </p>
        <p>
          Frame it as rewarding specific behaviors - loyalty, distance traveled,
          first visit, etc. Customers understand earned rewards and logical
          differences. A "thanks for traveling 50 miles" credit makes sense.
        </p>
        <br />
        <p>
          <strong>"How is this different from just giving discounts?"</strong>
        </p>
        <p>
          Targeted campaigns are strategic investments in customer retention and
          growth. Blast discounts are just leaving money on the table.
        </p>
      </SectionContent>
    </Section>

    <Section>
      <SectionTitle>Getting Started</SectionTitle>
      <SectionContent>
        <p>
          <strong>Try this simple win-back campaign:</strong>
        </p>
        <ol style={{ paddingLeft: '20px', margin: '12px 0' }}>
          <li style={{ margin: '8px 0' }}>
            Click the "Inactive" segment filter
          </li>
          <li style={{ margin: '8px 0' }}>
            Review the customer count that appears
          </li>
          <li style={{ margin: '8px 0' }}>
            Click "Send Campaign" in Quick Actions
          </li>
          <li style={{ margin: '8px 0' }}>
            Use message: "It's been a while! Here's $5 off your next visit"
          </li>
        </ol>
        <p>
          Even a simple reminder can bring people back. They might have just
          forgotten about you or gotten busy. Give them a reason to return.
        </p>
      </SectionContent>
    </Section>
  </>
);

// Keep the old export for backwards compatibility (deprecated)
export const smartInsightsHelpContent = {
  title: 'Smart Marketing Guide',
  heroSection: {
    title: 'Different Customers, Different Needs',
    text: 'Every successful business recognizes this simple truth. Airlines have loyalty tiers. Retailers send birthday discounts. Restaurants take care of regulars. Different customers respond to different messages - use that to your advantage.',
  },
  sections: [
    {
      key: 'how-it-works',
      title: 'How Targeted Campaigns Work',
      content: (
        <>
          <p>
            Connect your customer filtering with Quick Actions to create
            powerful, targeted campaigns that get results.
          </p>
          <FlowDiagram>
            <FlowStep>
              <StepNumber>1</StepNumber>
              <StepText>Filter Table</StepText>
            </FlowStep>
            <FlowArrow />
            <FlowStep>
              <StepNumber>2</StepNumber>
              <StepText>Quick Actions</StepText>
            </FlowStep>
            <FlowArrow />
            <FlowStep>
              <StepNumber>3</StepNumber>
              <StepText>Send Campaign</StepText>
            </FlowStep>
          </FlowDiagram>
          <p>
            Whatever filters you apply to the customer table automatically
            become your campaign audience. No extra steps needed.
          </p>
        </>
      ),
      expandable: false,
    },
    {
      key: 'why-matters',
      title: 'Why This Matters',
      content: (
        <>
          <p>
            <strong>People are creatures of habit.</strong> Once they stop
            coming, it gets easier to keep not coming. Don't let customers drift
            away.
          </p>
          <ul>
            <li>
              <strong>Better results</strong> - Send the right message to the
              right people instead of hoping a generic message works for
              everyone
            </li>
            <li>
              <strong>Lower costs</strong> - Stop wasting credits on messages to
              people who won't respond
            </li>
            <li>
              <strong>Customer retention</strong> - Stay top of mind so
              customers don't forget about you when planning their weekend
            </li>
          </ul>
          <CalloutBox>
            <CalloutText>
              <strong>Think about it:</strong> It's always cheaper to keep a
              customer than to win them back after they've left. A small
              thank-you to regulars costs less than a big discount to win back
              someone who's been gone for months.
            </CalloutText>
          </CalloutBox>
        </>
      ),
      expandable: false,
    },
    {
      key: 'win-back',
      title: 'Win-Back Campaigns',
      content: (
        <>
          <p>
            Re-engage customers who haven't visited recently. Life gets busy -
            help them remember why they loved coming.
          </p>
          <ul>
            <li>
              <strong>Filter:</strong> Customers inactive for 60+ days
            </li>
            <li>
              <strong>Message:</strong> "We miss you" with comeback offer
            </li>
            <li>
              <strong>Why it works:</strong> People get busy and forget. A
              friendly reminder with a small incentive breaks the inertia
            </li>
          </ul>
          <p>
            The longer someone stays away, the harder it is to come back. They
            might feel awkward, assume things have changed, or just develop new
            weekend routines. Reach out before they forget about you completely.
          </p>
        </>
      ),
      expandable: true,
    },
    {
      key: 'loyalty-rewards',
      title: 'Loyalty Rewards',
      content: (
        <>
          <p>
            Show appreciation to your best customers before they drift away.
          </p>
          <ul>
            <li>
              <strong>Filter:</strong> High attendance or high lifetime value
            </li>
            <li>
              <strong>Message:</strong> Thank you credit or exclusive perks
            </li>
            <li>
              <strong>Result:</strong> Increased retention and word-of-mouth
            </li>
          </ul>
          <p>
            Regular customers who feel appreciated stay regular customers. Those
            who feel taken for granted eventually stop making the effort. A
            simple "thanks for being a regular" goes a long way.
          </p>
        </>
      ),
      expandable: true,
    },
    {
      key: 'location-based',
      title: 'Location-Based Targeting',
      content: (
        <>
          <p>Maximize attendance by targeting based on distance.</p>
          <ul>
            <li>
              <strong>Local (0-10 miles):</strong> Last-minute reminders work
              best, weather-based promotions
            </li>
            <li>
              <strong>Nearby (11-60 miles):</strong> Standard event marketing,
              weekend promotions
            </li>
            <li>
              <strong>Regional (61-100 miles):</strong> Need advance notice and
              stronger incentives, weekend packages
            </li>
            <li>
              <strong>Distant (101+ miles, US):</strong> Target for major
              events, vacation packages with hotels
            </li>
            <li>
              <strong>International:</strong> Tourism packages, multi-day
              passes, partner with travel agencies
            </li>
          </ul>
        </>
      ),
      expandable: true,
    },
    {
      key: 'common-objections',
      title: 'Common Questions',
      content: (
        <>
          <p>
            <strong>"Isn't this unfair to other customers?"</strong>
          </p>
          <p>
            No. This is how every successful business operates. Different
            customers have different needs. A new customer needs directions and
            information. A regular wants recognition. Someone far away needs
            more notice. It's just good business sense.
          </p>
          <br />
          <p>
            <strong>
              "What if customers complain about different offers?"
            </strong>
          </p>
          <p>
            Frame it as rewarding specific behaviors - loyalty, distance
            traveled, first visit, etc. Customers understand earned rewards and
            logical differences. A "thanks for traveling 50 miles" credit makes
            sense.
          </p>
          <br />
          <p>
            <strong>"How is this different from just giving discounts?"</strong>
          </p>
          <p>
            Targeted campaigns are strategic investments in customer retention
            and growth. Blast discounts are just leaving money on the table.
          </p>
        </>
      ),
      expandable: true,
    },
    {
      key: 'getting-started',
      title: 'Getting Started',
      content: (
        <>
          <p>
            <strong>Try this simple win-back campaign:</strong>
          </p>
          <ol style={{ paddingLeft: '20px', margin: '12px 0' }}>
            <li style={{ margin: '8px 0' }}>
              Click the "Inactive" segment filter
            </li>
            <li style={{ margin: '8px 0' }}>
              Review the customer count that appears
            </li>
            <li style={{ margin: '8px 0' }}>
              Click "Send Campaign" in Quick Actions
            </li>
            <li style={{ margin: '8px 0' }}>
              Use message: "It's been a while! Here's $5 off your next visit"
            </li>
          </ol>
          <p>
            Even a simple reminder can bring people back. They might have just
            forgotten about you or gotten busy. Give them a reason to return.
          </p>
        </>
      ),
      expandable: true,
    },
  ],
};
